import { FC, useContext, useEffect, useState } from 'react';
import { CloseThin } from '~/components/Icons/fill';
import ReturnOfferStyled from './ReturnOfferStyled';
import Button from '~/components/Button';
import { useSelector, useDispatch } from 'react-redux';
import { ReduxState } from '~/types/redux';
import { InfoCircle, MapMarkerAlt } from '~/components/Icons/outline';
import { trackEvent } from '~/helpers/tracking';
import { useModal } from '~/hooks/useModal';
import { Offers } from '~/components/Icons/outline';
import { PAGE_ROUTES } from '~/constants/pages';
import { FuelContext } from '~/providers/FuelProvider';
import useFetch from '~/hooks/useFetch';
import { ResumeQuoteResponse } from '~/types/products';
import { updateAddress } from '~/redux/actions/address';
import { buildReturnVisitorHeaders } from '~/helpers/offers';
import useGlobalOfferMerchData from '~/hooks/useGlobalOfferMerchData';
import useReturnVisitorOfferEligibility from '~/hooks/useReturnVisitorOfferEligibility';
import { markReturnVisitorOfferShown } from '~/helpers/returnVisitorCookie';
import { setInstallationOptions, setResume, setTracking } from '~/redux/actions/common';
import { setNewRelicCustomAttribute } from '~/helpers/newrelic';
import { setCookie } from '~/helpers/cookie';
import Tooltip from '~/components/Tooltip';

interface ScreenProps {
  visible: boolean;
  hasAddress?: boolean;
  isResumeQuoteUser?: boolean;
  onClose?: () => void;
  apiVersion: string;
  buttons?: {
    select: {
      text: string;
      backgroundColor: string;
      borderColor: string;
      textColor: string;
      hoverBackgroundColor: string;
      hoverBorderColor: string;
      hoverTextColor: string;
      activeText?: string;
    };
  };
}

const ReturnOffer: FC<ScreenProps> = ({ visible, hasAddress, isResumeQuoteUser, onClose, apiVersion, buttons }) => {
  const { address } = useSelector((store: ReduxState) => store);
  const { closeModal } = useModal();
  const { router } = useContext(FuelContext);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const offerMerchandising = useGlobalOfferMerchData();
  const returnVisitorOfferEligibility = useReturnVisitorOfferEligibility();
  const merchandisingModal = offerMerchandising.data?.returnVisitorOffer?.modal;
  const isDataLoaded = Boolean(merchandisingModal);

  const currentPage = router.pathname;
  const techAvailable = address?.techAvailable;
  const isCopper = techAvailable === 'COPPER';
  const tooltipCopy = merchandisingModal?.tooltip?.trim();
  const shouldRenderTooltip = Boolean(tooltipCopy);

  useEffect(() => {
    if (!visible) return;
    markReturnVisitorOfferShown();
  }, [visible]);

  // Fetch quote data if user has quoteId (like ResumeModal does)
  const headers: HeadersInit = buildReturnVisitorHeaders(returnVisitorOfferEligibility, isCopper);
  const [isMonarchRVOStable, setIsMonarchRVOStable] = useState(false);
  const resumeQuote = useFetch<ResumeQuoteResponse>(`${apiVersion}/quote/resume`, { method: 'GET', headers }, true);
  // Fetch quote data if user has quoteId (like ResumeModal does)

  useEffect(() => {
    const rvoTimeout = setTimeout(() => {
      setIsMonarchRVOStable(true);
    }, 500);
    return () => clearTimeout(rvoTimeout);
  }, [returnVisitorOfferEligibility, hasAddress]);

  const handleButtonClick = () => {
    const modalClose = onClose || closeModal;

    // Use the hasAddress prop passed from parent, which considers both Redux address and cookie data
    const actualHasAddress = hasAddress || Boolean(address?.install?.address);

    if (actualHasAddress) {
      // User has address - behave like ResumeModal
      modalClose();
      dispatch(setResume('acceptedResume', true));
      dispatch(setResume('enabled', true));
      dispatch(setResume('viewedModal', true));
      dispatch(setResume('completed', false));
      setCookie('resumeEnabled', true, { otGroup: 'C0003' });

      // Keep this for landing pages - it's used to track resume across page navigations
      // Redux state won't persist across page loads, but sessionStorage will
      window.sessionStorage.setItem('seenResumeModal', 'true');

      router.replace(PAGE_ROUTES.RESUME);
      window.sessionStorage.removeItem('resumeModalActive');
    } else {
      // User doesn't have address - close modal
      modalClose();
    }

    trackEvent({
      action: 'elementClicked',
      data: {
        elementType: 'MODAL',
        location: currentPage,
        position: 'Return Offer Modal - Primary CTA',
        text: actualHasAddress ? merchandisingModal?.ctaText || 'BUY NOW' : 'ENTER YOUR ADDRESS',
        offerType: 'returnVisitorOffer',
      },
    });
  };

  useEffect(() => {
    // Only make API calls if the modal is actually visible
    if (!visible) return;

    if (!isResumeQuoteUser) return;

    if (!isMonarchRVOStable) return;

    // Set viewedModal flag in redux only
    dispatch(setResume('viewedModal', true));
    setLoading(true);
    (async () => {
      const data: ResumeQuoteResponse = await resumeQuote.fetch();

      if (data?.success) {
        window.sessionStorage.setItem('resumeResponse', JSON.stringify(data));
        dispatch(setResume('sweetenerSelected', data?.sweetenerSelected));
        dispatch(setResume('creditChecked', data?.creditChecked));
        dispatch(setResume('cartPage', data?.cartPage));
        dispatch(setTracking('accountUuid', data?.accountUuid));
        dispatch(setTracking('quoteNumber', data?.quoteNumber));
        dispatch(updateAddress('install', data?.serviceAddress));
        // Installation before credit: force self install to false
        dispatch(setInstallationOptions('availableInstallOptions', data?.installationOptions));
        if (data?.rvoQuoteId) {
          dispatch(setTracking('quoteId', data?.quoteId));
        }
        setNewRelicCustomAttribute([{ name: 'isResume', value: 'true' }]);
        setLoading(false);
        //Tracks time taken for the address to load
      } else {
        closeModal();
        window.sessionStorage.removeItem('resumeModalActive');
        router.replace(PAGE_ROUTES.ADDRESS_CHECK);
      }
    })();
  }, [visible, isResumeQuoteUser, isMonarchRVOStable]);

  // Don't render if not visible or data not loaded
  // Note: We don't call closeModal() here because it causes unnecessary re-renders
  // The cleanup effect above handles closing when unmounting
  if (!visible || !isDataLoaded) {
    return null;
  }

  trackEvent({
    action: 'elementViewed',
    data: {
      elementType: 'MODAL',
      location: currentPage,
      position: 'Return Offer Modal - Viewed',
      text: merchandisingModal?.headline || 'Return Visitor Offer',
      offerType: 'returnVisitorOffer',
    },
  });

  const handleClose = () => {
    const modalClose = onClose || closeModal;

    modalClose();

    trackEvent({
      action: 'elementClicked',
      data: {
        elementType: 'MODAL_CLOSE',
        text: 'X',
        location: currentPage,
        position: 'Return Offer Modal - Close',
        offerType: 'returnVisitorOffer',
      },
    });

    // Keep this session storage flag for immediate use and cross-page persistence
    // It ensures users who declined don't see the modal again, even after page navigation

    // Legacy Resume
    if (hasAddress) {
      window.sessionStorage.setItem('declinedResume', '1');

      // Also track in Redux state
      dispatch(setResume('acceptedResume', false));
      dispatch(setResume('enabled', false));
      dispatch(setResume('completed', false));
      window.sessionStorage.removeItem('resumeModalActive');
      router.replace(PAGE_ROUTES.ADDRESS_CHECK);
      return;
    }

    closeModal();
  };

  return (
    <ReturnOfferStyled>
      <CloseThin onClick={handleClose} className="return-offer-screen__close-button" />
      <div className="return-offer-screen__content-section">
        <div className="return-offer-screen__pre-header">
          <Offers className="return-offer-screen__offer-icon" />
          {merchandisingModal?.labelText}
        </div>
        <h3 className="return-offer-screen__heading">
          <p>{merchandisingModal?.preHeadline}</p>
          <p>{merchandisingModal?.headline}</p>
        </h3>
        <p className="return-offer-screen__subheading">
          {merchandisingModal?.subHeadline}
          {shouldRenderTooltip && (
            <Tooltip
              position="top"
              theme="light"
              html={<div style={{ width: 200 }} dangerouslySetInnerHTML={{ __html: tooltipCopy }} />}
            >
              <InfoCircle className="return-offer-screen__tooltip-icon" />
            </Tooltip>
          )}
        </p>

        {(hasAddress || address?.install?.address) && (
          <div className="return-offer-screen__address">
            <span className="return-offer-screen__address-label">
              <span className="return-offer-screen__address-icon">
                <MapMarkerAlt width="20" height="24" />
              </span>
              {address?.install?.address ? 'Service Address' : 'Getting address...'}
            </span>
            <span className="return-offer-screen__address-text">
              {address?.install?.address}
              {address?.install?.zip && `, ${address.install.zip}`}
            </span>
          </div>
        )}

        <Button
          className="return-offer-screen__btn"
          onClick={handleButtonClick}
          buttonConfig={buttons?.select}
          disabled={loading || !isMonarchRVOStable}
        >
          {loading || !isMonarchRVOStable
            ? 'Loading...'
            : hasAddress || address?.install?.address
            ? merchandisingModal?.ctaText || 'Buy Now'
            : 'ENTER YOUR ADDRESS'}
        </Button>
      </div>
    </ReturnOfferStyled>
  );
};

ReturnOffer.displayName = 'ReturnOffer';

export default ReturnOffer;
